// src/components/Widgets/widget-announcement.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock, faTimes, faCheck } from "@fortawesome/free-solid-svg-icons";
import Api from "../../services/api.service";
import LogoIcon from "../Logos/logo-icon";

/* ---------- helpers ---------- */
const isWeekend = (d = new Date()) => [0, 6].includes(d.getDay());
const withinDateWindow = (startDate, endDate, now = new Date()) => {
  const startOk = startDate ? now >= new Date(startDate + "T00:00:00") : true;
  const endOk = endDate ? now <= new Date(endDate + "T23:59:59") : true;
  return startOk && endOk;
};
const hashString = (str) => {
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = (h * 33) ^ str.charCodeAt(i);
  return (h >>> 0).toString(36);
};
const readI18n = (val, lang = "fr") => {
  if (!val) return "";
  if (typeof val === "string") return val;
  const l = String(lang).toLowerCase().slice(0, 2);
  return val[l] || val.fr || val.en || val.de || "";
};
const readI18nList = (val, lang = "fr") => {
  if (!val || typeof val !== "object") return [];
  const l = String(lang).toLowerCase().slice(0, 2);
  const arr = val[l] || val.fr || val.en || val.de || [];
  return Array.isArray(arr) ? arr : [];
};
const normalizeForLang = (cfg, lang) => {
  const norm = { ...(cfg || {}) };
  norm.title = readI18n(cfg?.title, lang);
  norm.messageHtml = readI18n(cfg?.messageHtml, lang);
  norm.ctaText = readI18n(cfg?.ctaText, lang);
  norm.dismissText = readI18n(cfg?.dismissText, lang);
  // NEW
  norm.bullets = readI18nList(cfg?.bullets, lang);
  norm.availabilityNote = readI18n(cfg?.availabilityNote, lang);
  return norm;
};

/* ---------- Loader ---------- */
export default function AnnouncementWidget() {
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let abort = false;
    (async () => {
      try {
        const res = await Api.getAnnouncementConfig();
        const json = res?.data;
        if (abort) return;
        if (json?.status && json?.data) setConfig(json.data);
        else setError(json?.error || "Invalid response");
      } catch (e) {
        if (!abort) setError(e?.message || "Network error");
      }
    })();
    return () => { abort = true; };
  }, []);

  if (error || !config) return null;
  return <AnnouncementWidgetInner config={config} />;
}

/* ---------- Inner ---------- */
function AnnouncementWidgetInner({ config }) {
  const { i18n } = useTranslation();
  const lang = (i18n?.language || "fr").toLowerCase().slice(0, 2);
  const L = useMemo(() => normalizeForLang(config, lang), [config, lang]);

  const {
    enabled = true,
    title,
    messageHtml,
    ctaText,
    ctaHref = "/contact",
    dismissText,
    weekendsOnly = true,
    startDate,
    endDate,
    delayMs = 300,
    showOncePerSession = true,
    localStorageKey,
    position = "bottom-right",
    accentClass,
    backgroundClass,
    showLogo = false,

    // NEW
    bullets = [],
    availabilityNote = "",
  } = L;

  const accent = accentClass || "bg-main";
  const background = backgroundClass || "bg-secondary";

  // include bullets & availabilityNote so dismissal resets if content changes
  const relevantForKey = JSON.stringify({
    title, messageHtml, ctaText, ctaHref, weekendsOnly,
    startDate: startDate || null, endDate: endDate || null,
    bullets, availabilityNote,
  });
  const computedKey = localStorageKey || `announcementWidget:${hashString(relevantForKey)}`;
  const storage = showOncePerSession ? sessionStorage : localStorage;
  const storageKey = `${computedKey}:dismissed`;

  const [dismissed, setDismissed] = useState(() => {
    try { return storage.getItem(storageKey) === "1"; } catch { return false; }
  });
  const [expanded, setExpanded] = useState(false);

  const [step, setStep] = useState(0);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!expanded) { setStep(0); return; }
    const t1 = setTimeout(() => setStep(1), 10);
    const t2 = setTimeout(() => setStep(2), 160);
    const t3 = setTimeout(() => setStep(3), 320);
    const t4 = setTimeout(() => setStep(4), 480);
    const t5 = setTimeout(() => setStep(5), 640);
    return () => { [t1, t2, t3, t4, t5].forEach(clearTimeout); };
  }, [expanded]);

  const appear = (s) =>
    `${step >= s ? "opacity-100 translate-y-0" : "opacity-0 translate-y-[6px] pointer-events-none"} transition-all duration-300 ease-out will-change-[opacity,transform]`;

  const shouldRender = useMemo(() => {
    if (!enabled) return false;
    if (dismissed) return false;
    if (weekendsOnly && !isWeekend()) return false;
    if (!withinDateWindow(startDate, endDate)) return false;
    return true;
  }, [enabled, dismissed, weekendsOnly, startDate, endDate]);

  useEffect(() => {
    if (!shouldRender) {
      setReady(false);
      setExpanded(false);
      return;
    }
    const t = setTimeout(() => {
      setReady(true);      // mount the whole popup after delay
      setExpanded(true);   // kick off the staged appear (title -> text -> bullets -> CTA -> note)
    }, delayMs);

    return () => {
      clearTimeout(t);
      setReady(false);
      setExpanded(false);
    };
  }, [shouldRender, delayMs]);

  // gate rendering on ready as well
  if (!shouldRender || !ready) return null;

  const handleClose = () => {
    try { storage.setItem(storageKey, "1"); } catch { }
    setExpanded(false);
    setTimeout(() => setDismissed(true), 250);
  };

  const isRight = position === "bottom-right";

  /* ---------- Centered variant (Tailwind) ---------- */
  if (position === "center") {
    const onOverlayClick = (e) => {
      if (e.currentTarget === e.target) handleClose();
    };

    return (
      <div
        className="fixed inset-0 z-[99999] bg-black/50 grid place-items-center p-6"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={onOverlayClick}
      >
        <article className="w-full max-w-[560px] bg-white text-[#1D1D1F] border border-black/10 shadow-2xl rounded-none relative">
          {showLogo && (
            <div
              className={`absolute left-1/2 -translate-x-1/2 -top-10 bg-white rounded-full p-2 shadow z-10 ${appear(1)}`}
              aria-hidden="true"
            >
              <LogoIcon width={56} height={56} />
            </div>
          )}

          {/* Close */}
          <button
            onClick={handleClose}
            aria-label={dismissText || "Close dialog"}
            className="absolute top-3 right-3 h-9 w-9 grid place-items-center bg-white border border-black/10 rounded-full text-[#1D1D1F] hover:bg-gray-50 transition"
            type="button"
          >
            <FontAwesomeIcon icon={faTimes} className="h-4 w-4" />
          </button>

          {/* Header */}
          <header className={`px-6 ${showLogo ? 'pt-8 md:pt-10' : 'pt-6'} transition-[padding] duration-300`}>
            <h2 className={`font-bold leading-tight text-[1.35rem] sm:text-[1.6rem] ${appear(1)}`}>
              {title}
            </h2>
            <div className="h-px bg-black/10 mt-4" />
          </header>

          {/* Body */}
          <section className="px-6 pt-4">
            <div
              className={`[&>*]:my-3 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 ${appear(2)}`}
              dangerouslySetInnerHTML={{ __html: messageHtml }}
            />
            {bullets.length > 0 && (
              <ul className={`mt-3 space-y-2 ${appear(3)}`} aria-label="Offer details">
                {bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-[0.98rem] leading-6">
                    <span className="text-[#292929] mt-0.5">
                      <FontAwesomeIcon icon={faCheck} className="h-4 w-4" />
                    </span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Footer */}
          <footer className="px-6 py-5 flex items-center justify-between gap-3">
            <small className={`text-sm text-[#6B6B6B] ${appear(5)}`}>
              {availabilityNote || "Subject to availability."}
            </small>
            <a
              href={ctaHref}
              onClick={handleClose}
              className={`inline-flex items-center font-bold text-base px-4 py-3 bg-main border border-main text-white hover:bg-main-dark hover:border-main-dark rounded-none transition ${appear(4)}`}
            >
              {ctaText || "Book now"}
            </a>
          </footer>
        </article>
      </div>
    );
  }

  /* ---------- Bottom left/right variant ---------- */
  return (
    <div
      className={`fixed z-[99999] ${isRight ? "right-4" : "left-4"} bottom-4 ${background} rounded-xl shadow-lg p-3 pb-2 max-w-sm w-[90vw] sm:w-[26rem] transition-all duration-300`}
      role="dialog"
      aria-live="polite"
      aria-label={title}
    >

      {showLogo && (
        <div
          className={`absolute ${isRight ? "right-4" : "left-4"} -top-10 bg-white rounded-full p-2 shadow ${appear(1)}`}
          aria-hidden="true"
        >
          <LogoIcon width={48} height={48} />
        </div>
      )}

      <div className="flex items-start gap-3">
        <div className={`${accent} text-white rounded-full p-2 shrink-0 shadow flex h-fit`} aria-hidden>
          <FontAwesomeIcon icon={faClock} className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className={`text-white text-sm sm:text-base font-semibold leading-snug ${appear(1)}`}>
            {title}
          </h3>
          <div className={`text-white/80 text-xs sm:text-sm`}>
            <div
              className={`[&>*]:my-2 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 ${appear(2)}`}
              dangerouslySetInnerHTML={{ __html: messageHtml }}
            />
            {bullets.length > 0 && (
              <ul className={`mt-2 space-y-1 ${appear(3)}`}>
                {bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="mt-0.5 text-white/90">
                      <FontAwesomeIcon icon={faCheck} className="h-3.5 w-3.5" />
                    </span>
                    <span className="text-white/90">{b}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className={`mt-2 flex items-center gap-2 ${appear(4)}`}>
            <a
              href={ctaHref}
              className={`${accent} text-white px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium shadow hover:opacity-90 transition`}
            >
              {ctaText}
            </a>
          </div>

          {availabilityNote && (
            <p className={`mt-1 text-[11px] text-white/70 ${appear(5)}`}>{availabilityNote}</p>
          )}

        </div>

        <button
          onClick={handleClose}
          className="text-white/80 hover:text-white p-1 -mt-1 -mr-2"
          aria-label={dismissText}
          type="button"
        >
          <FontAwesomeIcon icon={faTimes} className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}